// src/pages/GroupThreadPage.tsx — Sign-In System (Build 2) Group QuickMessage
// thread view (heather lane). Route /group-qm/:id. Prototype State 7.
//
// Ported from docs/build-plan/sign-in-system/slices/GroupThread.jsx to live TS.
// Today's thread for one group: closed segments render as one-line strips (their
// message bodies lazy-load on expand — never shipped in the thread response);
// the active segment renders its messages + composer + "Close segment & start
// fresh in this thread". Composer placeholder always names the active segment.
//
// Posting + closing require an open duty session (server enforces 403
// sign_in_required); the composer is disabled with a hint when the viewer is
// off duty. Reading only needs membership, so an off-duty member can still open
// and read the thread. Heather #7A5D8C only.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { TopBar } from "../components/ui";
import { G, GF, formatTime } from "../components/groupqm/groupTokens";

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

interface Msg {
  id: string;
  sender: { id: number; firstName: string; initials: string };
  body: string;
  createdAt: string;
  readByMe: boolean;
}
interface Segment {
  id: number;
  segIndex: number;
  openedAt: string;
  closedAt: string | null;
  closeReason: string | null;
  summary: string | null;
  messageCount: number;
  messages?: Msg[];
}
interface SignedInMember {
  id: number;
  firstName: string;
  lastName: string;
  initials: string;
  locationName: string | null;
  roleName: string | null;
}
interface ThreadResp {
  group: { id: number; displayName: string };
  thread: { id: number; segments: Segment[] };
  signedInMembers?: SignedInMember[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function GroupThreadPage() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const navigate = useNavigate();
  const { me } = useCurrentUser();
  const currentUserId = me?.hubUserId ?? -1;
  // SSO members can post anytime; PIN members still need an open duty session
  // (the server enforces the same rule, 403 sign_in_required otherwise).
  const canPost = me?.authMethod === "sso" || !!me?.dutySession?.active;

  const [data, setData] = useState<ThreadResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, Msg[]>>({});
  const [composeBody, setComposeBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch<ThreadResp>(`/api/group-qm/${groupId}/thread`);
      setData(r);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load this thread.");
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (!Number.isInteger(groupId) || groupId <= 0) {
      setError("Unknown group.");
      setLoading(false);
      return;
    }
    void load();
  }, [groupId, load]);

  const activeSegment = data?.thread.segments.find((s) => s.closedAt === null) ?? null;
  const closedSegments = (data?.thread.segments ?? []).filter((s) => s.closedAt !== null);

  // Mark the active segment read on open (fire-and-forget).
  useEffect(() => {
    if (!activeSegment) return;
    void apiFetch(`/api/group-qm/${groupId}/segments/${activeSegment.id}/read-all`, { method: "POST" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSegment?.id]);

  const handleExpandClosed = async (segId: number) => {
    if (expanded[segId]) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[segId];
        return next;
      });
      return;
    }
    try {
      const r = await apiFetch<{ messages: Msg[] }>(`/api/group-qm/${groupId}/segments/${segId}/messages`);
      setExpanded((prev) => ({ ...prev, [segId]: r.messages }));
    } catch {
      /* leave collapsed */
    }
  };

  const handleSend = async () => {
    const trimmed = composeBody.trim();
    if (!trimmed || trimmed.length > 200 || busy) return;
    setBusy(true);
    try {
      await apiFetch(`/api/group-qm/${groupId}/messages`, {
        method: "POST",
        body: JSON.stringify({ body: trimmed }),
      });
      setComposeBody("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't send.");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (busy || !activeSegment) return;
    setBusy(true);
    try {
      await apiFetch(`/api/group-qm/${groupId}/segments/${activeSegment.id}/close`, { method: "POST" });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't close the segment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <TopBar
        title={data?.group.displayName ?? "Group"}
        sub="Group messages"
        onBack={() => navigate("/")}
      />
      <div style={{ padding: "16px 16px 28px" }}>
        {loading && <div style={{ height: 200, borderRadius: 16, background: G.pageWarm, border: `1px solid ${G.hairlineLt}` }} aria-hidden="true" />}

        {!loading && error && (
          <div role="alert" style={{ padding: 12, borderRadius: 12, background: "#F8D9D2", border: "1px solid rgba(184,58,43,0.22)", fontFamily: GF.body, fontSize: 13, color: "#8A2A1F", fontWeight: 700 }}>
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <div style={{ background: G.card, border: `1px solid ${G.grpLt}`, borderRadius: 16, overflow: "hidden", position: "relative" }}>
            <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg, ${G.grp}, ${G.grpDk})` }} />

            <PresenceBlock members={data.signedInMembers ?? []} />

            {closedSegments.length === 0 && !activeSegment && (
              <div style={{ padding: "28px 16px", textAlign: "center", fontFamily: GF.serif, fontStyle: "italic", color: G.inkMute }}>
                No messages yet today.
              </div>
            )}

            {/* Closed segment strips (summary only; bodies lazy-load) */}
            {closedSegments.map((seg) => (
              <ClosedSegmentStrip
                key={seg.id}
                segment={seg}
                expandedMessages={expanded[seg.id]}
                onToggle={() => handleExpandClosed(seg.id)}
                currentUserId={currentUserId}
              />
            ))}

            {/* Active segment */}
            {activeSegment && (
              <>
                <div style={{ textAlign: "center", margin: "8px 16px 2px", position: "relative" }}>
                  <span style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: `1px dashed ${G.hairline}` }} />
                  <span style={{ position: "relative", background: G.card, padding: "0 10px", fontFamily: GF.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: "1.2px", color: G.inkMute, textTransform: "uppercase" }}>
                    Active · Segment {activeSegment.segIndex} · from {formatTime(activeSegment.openedAt)}
                  </span>
                </div>

                <MessageList messages={activeSegment.messages ?? []} currentUserId={currentUserId} />

                {canPost && activeSegment.messageCount > 0 && (
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={busy}
                    style={{
                      width: "calc(100% - 28px)", margin: "2px 14px 12px", padding: "9px 12px",
                      background: G.pageWarm, border: `1px dashed ${G.brassDk}`, borderRadius: 10,
                      fontFamily: GF.body, fontSize: 12, fontWeight: 700, color: G.brassDk, cursor: busy ? "wait" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6, letterSpacing: "0.4px",
                    }}
                  >
                    <CloseIcon size={12} /> Close segment &amp; start fresh in this thread
                  </button>
                )}

                <div style={{ padding: "10px 14px 14px", borderTop: `1px solid ${G.hairlineLt}`, background: G.cardSoft }}>
                  {canPost ? (
                    <>
                      <textarea
                        value={composeBody}
                        onChange={(e) => setComposeBody(e.target.value)}
                        maxLength={200}
                        placeholder={`Reply to Segment ${activeSegment.segIndex}…`}
                        aria-label={`Reply to Segment ${activeSegment.segIndex}`}
                        style={{
                          width: "100%", minHeight: 56, padding: "11px 12px", background: G.card,
                          border: `1px solid ${G.hairline}`, borderRadius: 12, fontFamily: GF.body, fontSize: 14,
                          color: G.ink, fontWeight: 500, lineHeight: 1.45, resize: "none", outline: "none", boxSizing: "border-box",
                        }}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                        <span aria-live="polite" style={{ fontFamily: GF.mono, fontSize: 10.5, fontWeight: 700, color: G.inkMute, letterSpacing: "0.4px" }}>
                          {composeBody.length} / 200
                        </span>
                        <button
                          type="button"
                          onClick={handleSend}
                          disabled={!composeBody.trim() || busy}
                          style={{
                            flex: 1, minHeight: 40, padding: "9px 14px", background: G.grp, color: "#fff", border: "none",
                            borderRadius: 12, fontFamily: GF.body, fontSize: 13.5, fontWeight: 800,
                            cursor: composeBody.trim() && !busy ? "pointer" : "not-allowed",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(122,93,140,0.22)",
                            opacity: composeBody.trim() && !busy ? 1 : 0.45,
                          }}
                        >
                          <SendIcon size={14} /> Send
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontFamily: GF.serif, fontStyle: "italic", fontSize: 13, color: G.inkSoft, textAlign: "center", padding: "6px 4px" }}>
                      Sign in for duty to post to this group.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presence — who in this group holds an open duty session right now (name +
// location + role). Mirrors the "Signed-In" pill on Home (HomePage.tsx), in the
// heather lane. Renders nothing when no one in the group is on duty.
// ---------------------------------------------------------------------------

function PresenceBlock({ members }: { members: SignedInMember[] }) {
  if (members.length === 0) return null;
  return (
    <div style={{ padding: "12px 14px 10px", borderBottom: `1px solid ${G.hairlineLt}`, background: G.cardSoft }}>
      <div style={{ fontFamily: GF.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: "1.2px", color: G.inkMute, textTransform: "uppercase", marginBottom: 8 }}>
        On duty now · {members.length}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {members.map((m) => (
          <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              width: 26, height: 26, borderRadius: 8,
              background: `linear-gradient(135deg, #9183A3, ${G.grp})`,
              color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: GF.serif, fontWeight: 700, fontSize: 10, flexShrink: 0,
            }}>
              {m.initials || (m.firstName || "?").slice(0, 2).toUpperCase()}
            </span>
            <span
              aria-label="Signed in for duty"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
                borderRadius: 999, background: G.grpBg, border: `1px solid ${G.grpLt}`,
                fontFamily: GF.body, fontSize: 10.5, fontWeight: 800, color: G.grpDk,
                letterSpacing: 0.2, whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: G.grp }} />
              Signed-In
            </span>
            <span style={{ fontFamily: GF.body, fontSize: 13, fontWeight: 700, color: G.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {`${m.firstName} ${m.lastName}`.trim() || "Colleague"}
              {m.locationName && (
                <span style={{ fontFamily: GF.serif, fontStyle: "italic", fontWeight: 600, color: G.inkSoft }}>{` · ${m.locationName}`}</span>
              )}
              {m.roleName && (
                <span style={{ fontFamily: GF.serif, fontStyle: "italic", fontWeight: 600, color: G.inkMute }}>{` · ${m.roleName}`}</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Closed segment strip — summary only, expands to lazy-loaded bodies
// ---------------------------------------------------------------------------

function ClosedSegmentStrip({
  segment, expandedMessages, onToggle, currentUserId,
}: {
  segment: Segment;
  expandedMessages: Msg[] | undefined;
  onToggle: () => void;
  currentUserId: number;
}) {
  const isExpanded = !!expandedMessages;
  return (
    <div>
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={`seg-${segment.id}-body`}
        onClick={onToggle}
        style={{
          margin: "6px 14px 4px", width: "calc(100% - 28px)", background: G.pageWarm, borderRadius: 10,
          padding: "8px 12px", fontFamily: GF.body, fontSize: 11, fontWeight: 700, letterSpacing: "0.6px",
          color: G.inkSoft, display: "flex", alignItems: "center", gap: 8, border: `1px dashed ${G.hairline}`,
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ fontFamily: GF.mono, fontSize: 10, color: G.inkMute, whiteSpace: "nowrap" }}>
          SEG {segment.segIndex} · {formatTime(segment.openedAt)}–{segment.closedAt ? formatTime(segment.closedAt) : ""}
        </span>
        <span style={{ fontFamily: GF.serif, fontStyle: "italic", color: G.ink, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {segment.summary ?? "(closed)"}
        </span>
        <span style={{ marginLeft: "auto", color: G.brassDk, fontWeight: 800 }}>{isExpanded ? "⌃" : "›"}</span>
      </button>
      {isExpanded && (
        <div id={`seg-${segment.id}-body`} style={{ padding: "0 4px" }}>
          <MessageList messages={expandedMessages ?? []} currentUserId={currentUserId} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message list
// ---------------------------------------------------------------------------

function MessageList({ messages, currentUserId }: { messages: Msg[]; currentUserId: number }) {
  if (messages.length === 0) {
    return (
      <div style={{ padding: "10px 16px 12px", fontFamily: GF.serif, fontStyle: "italic", fontSize: 13, color: G.inkMute }}>
        No messages in this segment yet.
      </div>
    );
  }
  return (
    <div style={{ padding: "6px 14px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
      {messages.map((msg) => {
        const isMe = msg.sender.id === currentUserId;
        return (
          <div key={msg.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{
              width: 26, height: 26, borderRadius: 8,
              background: isMe ? `linear-gradient(135deg, #9183A3, ${G.grp})` : "linear-gradient(135deg, #D9B466, #A6771C)",
              color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: GF.serif, fontWeight: 700, fontSize: 10, flexShrink: 0, marginTop: 1,
            }}>
              {msg.sender.initials || (msg.sender.firstName || "?").slice(0, 2).toUpperCase()}
            </span>
            <div style={{ flex: 1, background: isMe ? G.cardSoft : G.grpSurf, borderRadius: 12, padding: "8px 12px 9px", border: `1px solid ${isMe ? G.hairline : G.grpLt}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
                <span style={{ fontFamily: GF.body, fontSize: 11.5, fontWeight: 800, color: isMe ? G.grpDk : G.ink, letterSpacing: "-0.1px" }}>
                  {isMe ? "You" : msg.sender.firstName}
                </span>
                <span style={{ fontFamily: GF.mono, fontSize: 9.5, color: G.inkMute, letterSpacing: "0.4px" }}>
                  {formatTime(msg.createdAt)}
                </span>
              </div>
              <div style={{ fontFamily: GF.serif, fontSize: 14, fontWeight: 500, color: G.ink, lineHeight: 1.4 }}>
                {msg.body}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SendIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}
function CloseIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
